import { GridCol, GridContainer, Icon, ICONS, useIsWindowBiggerThan } from '@bob/library'
import { PageContext } from '@websrc/App/AppContext'
import { AppProvider } from '@websrc/App/AppProvider'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { ExitPopup } from '@websrc/App/Components/ExitPopup/ExitPopup'
import { pathToPageName } from '@websrc/App/Helpers/pathToPageName'
import { useCoralBrickBackground } from '@websrc/App/Hooks/useCoralBrickBackground'
import { MtmParams } from '@websrc/App/Hooks/useMTMParams'
import { useRenderExitPopup } from '@websrc/App/Hooks/useRenderExitPopup'
import { useVisibleRange } from '@websrc/App/Hooks/useVisibleRange'
import { ExitPopupData } from '@websrc/DataMappers/mapExitPopup'
import { mapStandardPage } from '@websrc/DataMappers/mapStandardPage'
import { Hero } from '@websrc/Hero/Hero'
import { PageModal } from '@websrc/Modal/PageModal'
import { SpotCollapse } from '@websrc/Spot/Collapse/SpotCollapse'
import { SpotLinkAnchor } from '@websrc/Spot/Link/SpotLinkAnchor'
import React from 'react'
import { match } from 'ts-pattern'

import { standardPage } from './Standardpage.css'
import * as SC from './StandardPage.style'

export interface PageRenderProps {
  page: ReturnType<typeof mapStandardPage>
  exitPopup?: ExitPopupData
}

export interface StandardPageProps {
  pageContext: PageContext & PageRenderProps
}

export function StandardPageRender({ page, exitPopup }: PageRenderProps) {
  const { EndCursor: VisibleEndCursor, StartCursor, isVisible } = useVisibleRange()
  const isDesktop = useIsWindowBiggerThan(1024)
  const exitPopupDisabled = page.disableExitPopup
  const renderExitPopup = useRenderExitPopup(!exitPopupDisabled) && exitPopup

  useCoralBrickBackground(page.isCoralPage)

  return (
    <>
      <StartCursor />
      {renderExitPopup && <ExitPopup {...exitPopup} />}
      {page.modals?.map((modal, i) => (
        <PageModal key={`modal-${i}`} modal={modal} />
      ))}
      {!!page.hero_image?.thumbnail && (
        <GridContainer style={!isDesktop ? { padding: 0 } : {}}>
          <GridCol noGutter={!isDesktop}>
            <Asset
              altText={page.hero_image.altText}
              className={standardPage.heroImage}
              objectFit={'cover'}
              thumbnail={page.hero_image.thumbnail}
            />
          </GridCol>
        </GridContainer>
      )}
      <SC.SpotWrapper overlayOnHeroImage={!!page.hero_image?.thumbnail}>
        <Hero hero={page.hero} />
        {page.spots.map((_, i) => (
          <SpotCollapse key={`spot-parent-${i}`} spotIndex={i} spots={page.spots} />
        ))}
      </SC.SpotWrapper>
      {page.sticky_cta?.cta_text && (
        <SC.StickyWrapper show={isVisible}>
          <SpotLinkAnchor
            combination_of_mtm_params={page.sticky_cta.combination_of_mtm_params}
            ctaType={'primary'}
            link={page.sticky_cta}
            mtm_params={page.sticky_cta.mtm_params as MtmParams}
          >
            <Icon icon={ICONS.ARROW_RIGHT} size={16} />
            <span>{page.sticky_cta.cta_text}</span>
          </SpotLinkAnchor>
        </SC.StickyWrapper>
      )}
      <VisibleEndCursor />
    </>
  )
}

const getTemplateNameFromUrl = (url: string) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`

  return match(normalizedUrl)
    .with('/', () => 'home')
    .with('/grants-and-funding', () => 'misc_shop')
    .with('/learningsystem', () => 'marketing_discover')
    .with('/about-us/contact-us', () => 'solution_consultants_forms')
    .when(
      (u) => u.startsWith('/downloads'),
      () => 'downloads'
    )
    .otherwise(() => 'standard_page')
}

export default function StandardPage({ pageContext }: StandardPageProps) {
  const { page, appContext } = pageContext
  let [siteSection, pageName] = pathToPageName(appContext.path)
  let pageType = 'standard-pages'

  if (pageContext.page.is_marketing_page) {
    appContext.topBarOnlyShowsLogo = true
    appContext.hideTopbarAlerts = true
    if (appContext.footer) {
      appContext.footer.hideNavigation = true
      appContext.footer.hideFooterCTA = true
      appContext.footer.hideFooterColor = true
    }
  }

  // HomePage!
  if (pageName === 'legoeducation:') {
    pageType = 'home'
    siteSection += 'home'
    pageName += 'home'
  }

  if (siteSection === 'legoeducation:downloads') {
    pageType = 'downloads'
  }

  appContext.showCoralTopbar = page.showCoralTopbar
  appContext.showCoralFooter = page.showCoralFooter

  return (
    <AppProvider
      {...appContext}
      hideCta={page.hideCta}
      matomoPageTemplate={getTemplateNameFromUrl(page.url)}
      pageName={pageName}
      pageType={pageType}
      showCoralFooter={appContext.showCoralFooter}
      showCoralTopbar={appContext.showCoralTopbar}
      siteSection={siteSection}
    >
      <StandardPageRender exitPopup={appContext.exitPopup} page={page} />
    </AppProvider>
  )
}
